export type TrainingEntrySource = 'navbar' | 'recent_tile' | 'chat_starter' | 'chat_suggestion';
export type TrainingOpening = Readonly<{ eventId: string; source: TrainingEntrySource }>;
const RETRY_DELAYS_MS = [1000, 4000];

export function newTrainingEntryId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** One tracker lives at the App level, so screen remounts cannot create entries. */
export class TrainingEntryTracker {
  private opening: TrainingOpening | null = null;
  private reported = false;
  private inFlight = false;
  private automaticRetries = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly report: (eventId: string, source: TrainingEntrySource) => Promise<unknown>,
    private readonly createId: () => string = newTrainingEntryId,
    private readonly onError: (error: unknown) => void = (error) => console.error('Training entry report failed:', error),
  ) {}

  open(source: TrainingEntrySource): TrainingOpening | null {
    if (this.opening) return null;
    this.opening = { eventId: this.createId(), source };
    this.reported = false;
    this.inFlight = false;
    this.automaticRetries = 0;
    return this.opening;
  }

  leave(): void {
    this.cancelRetry();
    this.opening = null;
    this.reported = false;
    this.inFlight = false;
    this.automaticRetries = 0;
  }

  overviewReady(eventId: string): void {
    const opening = this.opening;
    if (!opening || opening.eventId !== eventId || this.reported || this.inFlight) return;
    this.cancelRetry();
    this.inFlight = true;
    let request: Promise<unknown>;
    try {
      request = this.report(opening.eventId, opening.source);
    } catch (error) {
      this.reportFailed(opening, error);
      return;
    }
    void request.then(
      () => {
        if (this.opening !== opening) return;
        this.inFlight = false;
        this.reported = true;
      },
      (error) => {
        this.reportFailed(opening, error);
      },
    );
  }

  private cancelRetry(): void {
    if (this.retryTimer === null) return;
    clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private reportFailed(opening: TrainingOpening, error: unknown): void {
    if (this.opening === opening) {
      this.inFlight = false;
      if (this.automaticRetries < RETRY_DELAYS_MS.length) {
        const delay = RETRY_DELAYS_MS[this.automaticRetries];
        this.retryTimer = setTimeout(() => {
          this.retryTimer = null;
          if (this.opening !== opening) return;
          this.automaticRetries += 1;
          this.overviewReady(opening.eventId);
        }, delay);
      }
    }
    this.onError(error);
  }
}
